'use strict';

Object.defineProperty(exports, Symbol.toStringTag, { value: 'Module' });

const react = require('@emotion/react');
const styleEncapsulation = require('@zapier/style-encapsulation');
const React = require('react');
const hooks_useClickOutside = require('../../../hooks/useClickOutside.cjs.js');
const theme_Animation = require('../../../theme/Animation.cjs.js');
const theme_Colors = require('../../../theme/Colors.cjs.js');
const theme_Shadows = require('../../../theme/Shadows.cjs.js');
require('../../../theme/Typography.cjs.js');
const theme_LegacyTypography = require('../../../theme/LegacyTypography.cjs.js');
const components_display_Avatar_Avatar = require('../../display/Avatar/Avatar.cjs.js');
const components_display_TooltipWrapper_TooltipWrapper = require('../../display/TooltipWrapper/TooltipWrapper.cjs.js');
const components_display_Heading_Heading = require('../../display/Heading/Heading.cjs.js');
require('../../../utils/heading-level-context/HeadingLevelContext.cjs.js');
const components_display_Icon_Icon = require('../../display/Icon/Icon.cjs.js');
const components_display_Tag_Tag = require('../../display/Tag/Tag.cjs.js');
const components_forms_Menu_Menu = require('../../forms/Menu/Menu.cjs.js');
const components_forms_Menu_MenuItem = require('../../forms/Menu/MenuItem.cjs.js');
const components_navigation_Button_Button = require('../../navigation/Button/Button.cjs.js');
const components_navigation_Link_Link = require('../../navigation/Link/Link.cjs.js');
const components_patterns_ServiceIcons_ServiceIcons = require('../../patterns/ServiceIcons/ServiceIcons.cjs.js');
const components_marketing_ZapCard_useResizeObserver = require('./useResizeObserver.cjs.js');
const components_marketing_ZapCard_utils_darkenColor = require('./utils/darkenColor.cjs.js');
const styleMixins_reset = require('../../../style-mixins/reset.cjs.js');

const DEFAULT_COLOR = "#fffdf9";
const isDefaultColor = (color) => {
  return color === DEFAULT_COLOR;
};
const getDefaultComputedColors = () => ({
  backgroundColor: DEFAULT_COLOR,
  backgroundColorDark: "rgb(0, 0, 0, 0.25)"
});
const getComputedColors = (color) => {
  if (isDefaultColor(color) || !color) {
    return getDefaultComputedColors();
  }
  try {
    return {
      backgroundColor: components_marketing_ZapCard_utils_darkenColor.darkenColor(color, { contrast: 3 }),
      backgroundColorDark: components_marketing_ZapCard_utils_darkenColor.darkenColor(color, {
        contrast: 4.5,
        minimumLightnessDistance: 10
      })
    };
  } catch (err) {
    return getDefaultComputedColors();
  }
};
const hasMenu = (menuItems) => {
  return (menuItems || []).length > 0;
};
const CARD_MAX_WIDTH = 375;
const SMALL_LINE_MAX_WIDTH = 770;
const MEDIUM_LINE_MAX_WIDTH = 615;
const ANIMATION_TRANSITION_VALUE = "all 150ms ease-in-out";
const isCardLayout = (computedState) => {
  return computedState.layout === "card";
};
const isLineLayout = (computedState) => {
  return computedState.layout === "line";
};
const isMegaLayout = (computedState) => {
  return computedState.layout === "mega";
};
const isCardLayoutWidth = (computedState) => {
  return computedState.width <= CARD_MAX_WIDTH;
};
const isWideLayoutWidth = (computedState) => computedState.width > SMALL_LINE_MAX_WIDTH;
const isMediumLineLayoutWidth = (computedState) => {
  return computedState.width > CARD_MAX_WIDTH && computedState.width <= MEDIUM_LINE_MAX_WIDTH;
};
const isLargeLineLayoutWidth = (computedState) => {
  return computedState.width > MEDIUM_LINE_MAX_WIDTH;
};
const computeLineLayoutGrid = (computedState, props) => {
  if (isLargeLineLayoutWidth(computedState)) {
    return react.css`
      grid-template-columns: max-content max-content 1fr max-content max-content max-content;
      grid-template-areas:
        'services title title title label cta menu'
        '.        ${props.authorName ? "author" : "meta"}  meta  meta   meta     .   .';
    `;
  }
  return react.css`
    grid-template-columns: max-content 1fr max-content;
    grid-template-areas: 'services title cta';
  `;
};
const isCardOrMegaLayout = (computedState) => {
  return isCardLayout(computedState) || isMegaLayout(computedState);
};
const shouldBeColored = (computedState) => {
  return isCardOrMegaLayout(computedState) && !isDefaultColor(computedState.backgroundColor);
};
const StyleVars = {
  cardPadding: "20px",
  cardHeaderHeight: "40px",
  cardFooterHeight: "55px",
  megaPaddingLeft: "30px",
  megaPaddingTop: "38px",
  servicesHeight: "40px",
  servicesHeightMegaCard: "60px"
};
const centerLineCardStyles = react.css`
  ${styleMixins_reset.boxSizing};
  display: flex;
  align-items: center;
  min-height: ${StyleVars.servicesHeight};
`;
const Styles = styleEncapsulation.labelStyles("ZapCard", {
  root: (props, computedState) => [
    react.css`
      ${styleMixins_reset.boxSizing};
      min-width: 270px;
    `,
    // Pad area above card to render absolutely-positioned
    // menu and / or author.
    (isCardOrMegaLayout(computedState) || isLineLayout(computedState) && isMediumLineLayoutWidth(computedState)) && (hasMenu(props.menuItems) || props.authorName) && react.css`
        padding-top: ${StyleVars.cardHeaderHeight};
      `
  ],
  inner: (computedState, props) => [
    !shouldBeColored(computedState) && react.css`
        border: 1px solid ${theme_Colors.Colors.GrayWarm3};
      `,
    react.css`
      ${styleMixins_reset.boxSizing};
      position: relative;
      box-shadow: none;
      transition: box-shadow 150ms ${theme_Animation.Animation.transitionTimingFunction};
      border-radius: 5px;
      background-color: ${DEFAULT_COLOR};
      // Default to undefined.
      --ZapCard-text-color: ${theme_Colors.Colors.GrayWarm10};

      // This is the overlay and footer background color. A pseudo
      // element is used to facilitate using CSS Grid for the disparate
      // values for the 'layout' prop.
      &::before {
        pointer-events: none;
        content: '';
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        transition: ${ANIMATION_TRANSITION_VALUE};
      }

      &::after {
        opacity: 0;
        content: '';
        position: absolute;
        inset: -1px;
        border-radius: 4px;
        transition: opacity 150ms ease-in-out;
      }
      @media (hover: hover) {
        &:hover,
        &:focus-within {
          box-shadow: ${theme_Shadows.Shadows.elevation5};
        }
      }
    `,
    props.shouldOverlayLink && react.css`
        &:after {
          border: 2px solid ${theme_Colors.Colors.UiPrimaryStrongest};
        }
      `,
    isCardOrMegaLayout(computedState) && react.css`
        --ZapCard-cta-color: ${theme_Colors.Colors.GrayWarm6};
        --ZapCard-cta-opacity: 0;
        @media (hover: hover) {
          &:hover,
          &:focus-within {
            --ZapCard-cta-color: ${theme_Colors.Colors.UiPrimaryStrongest};
            --ZapCard-cta-opacity: 1;
          }
        }

        &::before {
          height: ${StyleVars.cardFooterHeight};
          border-radius: 0 0 4px 4px;
          background-color: ${theme_Colors.Colors.GrayWarm2};
        }

        @media (hover: hover) {
          &:hover::before,
          &:focus-within::before {
            background-color: ${theme_Colors.Colors.UiPrimaryWeakest};
          }
          &:focus-within::after {
            opacity: 1;
          }
        }
      `,
    shouldBeColored(computedState) && react.css`
        --ZapCard-cta-color: ${theme_Colors.Colors.PrimeWhite};
        --ZapCard-text-color: ${theme_Colors.Colors.PrimeWhite};
        background-color: ${computedState.backgroundColor};

        &::before {
          background-color: ${computedState.backgroundColorDark};
        }

        @media (hover: hover) {
          &:hover,
          &:focus-within {
            --ZapCard-cta-color: ${theme_Colors.Colors.PrimeWhite};
          }

          &:hover,
          &:focus-within {
            box-shadow: ${theme_Shadows.Shadows.elevation10};
          }

          &:hover::before,
          &:focus-within::before {
            background-color: ${computedState.backgroundColorDark};
          }
          &:focus-within::after {
            border: 2px solid ${computedState.backgroundColorDark};
          }
        }
      `,
    isLineLayout(computedState) && react.css`
        &::before {
          top: 0;
          right: 0;
          bottom: 0;
          left: 0;
          border-radius: inherit;
        }
        @media (hover: hover) {
          &:hover::before,
          &:focus-within::before {
            background-color: ${theme_Colors.Colors.UiPrimaryWeakest};
          }
        }
      `,
    isLineLayout(computedState) && react.css`
        div div div span[data-color] {
          transition: ${ANIMATION_TRANSITION_VALUE};
        }

        @media (hover: hover) {
          &:hover,
          &:focus-within {
            div div div span[data-color] {
              background-color: ${theme_Colors.Colors.PrimeWhite};
            }
          }
          &:focus-within::after {
            opacity: 1;
          }
        }
      `,
    isLineLayout(computedState) && computedState.isExpanded && react.css`
        &::before {
          content: '';
          display: none;
        }
      `
  ],
  layout: (computedState, props) => [
    react.css`
      ${styleMixins_reset.boxSizing};
      display: grid;
      position: relative;
    `,
    isCardOrMegaLayout(computedState) && react.css`
        label: -card;
        min-height: 219px;
        grid-template-columns: 1fr max-content;
        grid-template-rows: ${StyleVars.servicesHeight} 1fr ${StyleVars.cardFooterHeight};
        gap: 10px;
        align-items: center;
        grid-template-areas:
          'services label'
          'title title'
          'meta cta';
        padding: ${StyleVars.cardPadding} ${StyleVars.cardPadding} 0;
      `,
    isMegaLayout(computedState) && react.css`
        label: -mega;
        min-height: 345px;
        padding: ${StyleVars.megaPaddingTop} ${StyleVars.cardPadding} 0
          ${StyleVars.megaPaddingLeft};
        grid-template-rows: ${StyleVars.servicesHeightMegaCard} 1fr ${StyleVars.cardFooterHeight};
      `,
    isLineLayout(computedState) && react.css`
        label: -line;
        ${computeLineLayoutGrid(computedState, props)}
        gap: 2px 20px;
        align-items: center;
        min-height: 80px;
        padding: ${StyleVars.cardPadding};
      `
  ],
  titleArea: (computedState) => [
    react.css`
      ${styleMixins_reset.boxSizing};
      grid-area: title;
    `,
    isLineLayout(computedState) && react.css`
        ${centerLineCardStyles}
        align-items: inherit;
      `
  ],
  title: (computedState) => [
    react.css`
      ${styleMixins_reset.baseReset};
      ${theme_LegacyTypography.LegacyTypography.subHeader3Bold};
      color: var(--ZapCard-text-color, ${theme_Colors.Colors.GrayWarm10});
      // All of the properties below this are necessary
      // for line clamping to work. Even though the properties
      // have 'webkit' in them, they work cross-browser.
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
      overflow: hidden;
      resize: none;
    `,
    isMegaLayout(computedState) && react.css`
        -webkit-line-clamp: 5;
      `,
    isMegaLayout(computedState) && react.css`
        ${isWideLayoutWidth(computedState) ? theme_LegacyTypography.LegacyTypography.pageHeader7 : theme_LegacyTypography.LegacyTypography.pageHeader9}
      `,
    isLineLayout(computedState) && react.css`
        ${theme_LegacyTypography.LegacyTypography.paragraph1ShortBold}
        letter-spacing: 0 !important;
      `
  ],
  // Overlay link on top of content to prevent nesting
  // interactive elements.
  link: react.css`
    ${styleMixins_reset.interactiveReset};
    ${theme_LegacyTypography.LegacyTypography.paragraph1ShortBold};
    display: block;
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
  `,
  ctaArea: (computedState, props) => [
    react.css`
      ${styleMixins_reset.boxSizing};
      grid-area: cta;
      color: var(--ZapCard-text-color, var(--ZapCard-cta-color));
      position: relative;
      z-index: 3;
    `,
    isCardOrMegaLayout(computedState) && [
      react.css`
        display: grid;
        grid-template-columns: max-content max-content;
        justify-content: end;
        align-items: center;
        transition: ${ANIMATION_TRANSITION_VALUE};
      `,
      props.shouldOverlayLink && react.css`
          // Entire card is clickable; let these events "bleed through."
          pointer-events: none;
        `
    ],
    isLineLayout(computedState) && react.css`
        ${centerLineCardStyles};
      `
  ],
  ctaLink: (computedState) => [
    react.css`
      ${styleMixins_reset.boxSizing};
      display: flex;
      align-items: center;
      ${theme_LegacyTypography.LegacyTypography.smallPrint1Bold};
      color: var(--ZapCard-cta-color);
      transition: ${ANIMATION_TRANSITION_VALUE};
      text-decoration: none;
      outline: 1px solid transparent;
      gap: 10px;
      transition: all 150ms ${theme_Animation.Animation.transitionTimingFunction};

      // Balances out CTA right arrow icon, which
      // has 3px padding-right built in to the SVG
      padding-left: 3px;

      &:focus {
        transition: all 300ms ease-in-out;
        outline-color: ${theme_Colors.Colors.UiPrimaryStrongest};
      }

      &:focus,
      &:hover {
        color: ${theme_Colors.Colors.BrandNight};
      }
    `,
    isCardOrMegaLayout(computedState) && react.css`
        color: var(--ZapCard-cta-color);
      `,
    isMegaLayout(computedState) && react.css`
        ${theme_LegacyTypography.LegacyTypography.paragraph3Bold};
      `
  ],
  ctaIcon: (computedState) => [
    isLineLayout(computedState) && react.css`
        display: none;
      `
  ],
  menuArea: (computedState) => [
    react.css`
      ${styleMixins_reset.boxSizing};
      position: relative;
      z-index: 4;
    `,
    // Ensure that opened menus render over top of closed menus.
    computedState.isMenuOpen && react.css`
        z-index: 5;
      `,
    isCardOrMegaLayout(computedState) && react.css`
        label: -card;
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 100%;
        right: 0px;
        height: ${StyleVars.cardHeaderHeight};
      `,
    isLineLayout(computedState) && react.css`
        label: -line;
        grid-area: menu;
        // Tighten gutter
        margin-left: -10px;
        ${centerLineCardStyles};
      `,
    isLineLayout(computedState) && isMediumLineLayoutWidth(computedState) && react.css`
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 100%;
        right: 0px;
        height: ${StyleVars.cardHeaderHeight};
      `
  ],
  menuToggle: react.css`
    ${styleMixins_reset.interactiveReset};
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${theme_Colors.Colors.GrayWarm6};
    &:hover,
    &:focus,
    &[aria-expanded='true'] {
      color: ${theme_Colors.Colors.UiPrimaryStrongest};
    }
  `,
  menu: (computedState) => [
    react.css`
      ${styleMixins_reset.boxSizing};
      position: absolute;
      width: 270px;
      background: ${theme_Colors.Colors.PrimeWhite};
      border: 1px solid ${theme_Colors.Colors.GrayWarm4};
      border-radius: 3px;
      box-shadow: ${theme_Shadows.Shadows.elevation20};
      // Subtract a pixel to ensure card coverage.
      top: calc(100% - 1px);
      right: -1px;
    `,
    isLineLayout(computedState) && isLargeLineLayoutWidth(computedState) && react.css`
        top: 100%;
        right: calc(-${StyleVars.cardPadding} - 1px);
      `
  ],
  menuInner: react.css`
    ${styleMixins_reset.boxSizing};
    padding: 10px;
    color: ${theme_Colors.Colors.GrayWarm9};
  `,
  menuItemInner: react.css`
    ${styleMixins_reset.boxSizing};
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 8px;
    align-items: center;
  `,
  menuItemText: react.css`
    ${styleMixins_reset.boxSizing};
    overflow: hidden;
    text-overflow: ellipsis;
  `,
  labelArea: (computedState) => [
    react.css`
      ${styleMixins_reset.boxSizing};
      display: flex;
      grid-area: label;
      position: relative;
      z-index: 3;
      gap: 10px;
      align-items: center;
    `,
    isCardOrMegaLayout(computedState) && react.css`
        text-align: right;
        justify-content: end;
      `,
    isLineLayout(computedState) && react.css`
        ${centerLineCardStyles};
        gap: 20px;
      `,
    isLineLayout(computedState) && isMediumLineLayoutWidth(computedState) && react.css`
        display: none;
      `
  ],
  servicesArea: [
    react.css`
      ${styleMixins_reset.boxSizing};
      grid-area: services;
      position: relative;
      z-index: 3;
      align-self: baseline;
    `
  ],
  metaInfoArea: (computedState) => [
    react.css`
      ${styleMixins_reset.boxSizing};
      grid-area: meta;
      ${theme_LegacyTypography.LegacyTypography.smallPrint3Bold};
      font-size: 14px;
      line-height: 20px;
      color: var(--ZapCard-text-color, ${theme_Colors.Colors.GrayWarm10});
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    `,
    isMegaLayout(computedState) && react.css`
        font-size: 16px;
        line-height: 22px;
      `,
    isCardOrMegaLayout(computedState) && react.css`
        pointer-events: none;
      `,
    isLineLayout(computedState) && react.css`
        line-height: 20px;
      `,
    isLineLayout(computedState) && isMediumLineLayoutWidth(computedState) && react.css`
        display: none;
      `
  ],
  authorArea: (computedState) => [
    react.css`
      ${styleMixins_reset.boxSizing};
      ${theme_LegacyTypography.LegacyTypography.smallPrint1};
      gap: 10px;
    `,
    isCardOrMegaLayout(computedState) && react.css`
        label: -card;
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 100%;
        left: 0px;
        height: ${StyleVars.cardHeaderHeight};
      `,
    isLineLayout(computedState) && react.css`
        label: -line;
        grid-area: author;
        ${centerLineCardStyles};
      `,
    isLineLayout(computedState) && isLargeLineLayoutWidth(computedState) && react.css`
        gap: 5px;
        font-size: 14px;
        min-height: auto;
        position: relative;

        // Simple but somewhat magical way to add middot.
        // Gets the job done, but might refactor if more
        // of these middot separators are needed later.
        &:not(:empty)::after {
          content: '•';
          position: absolute;
          font-size: 16px;
          right: -14px;
          bottom: 2px;
          opacity: 0.4;
        }
      `,
    isLineLayout(computedState) && isMediumLineLayoutWidth(computedState) && react.css`
        display: flex;
        align-items: center;
        position: absolute;
        bottom: 100%;
        left: 0px;
        height: ${StyleVars.cardHeaderHeight};
      `
  ],
  authorContent: (computedState) => [
    react.css`
      ${styleMixins_reset.boxSizing};
      display: grid;
      grid-template-columns: min-content 1fr;
      gap: inherit;
      align-items: center;
      color: ${theme_Colors.Colors.GrayWarm9};
    `,
    react.css`
      position: relative;
      z-index: ${isLineLayout(computedState) && !isMediumLineLayoutWidth(computedState) ? 3 : 4};
    `
  ],
  authorByline: (computedState) => [
    react.css`
      ${styleMixins_reset.baseReset};
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      max-width: 22ch;
    `,
    isLineLayout(computedState) && react.css`
        max-width: 15ch;
      `
  ],
  authorName: react.css`
    ${styleMixins_reset.boxSizing};
    color: ${theme_Colors.Colors.GrayWarm9};
    font-weight: 700;
  `,
  summary: react.css`
    ${styleMixins_reset.interactiveReset};
    display: block;
    position: absolute;
    z-index: 2;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    opacity: 0;
  `,
  detailsContent: react.css`
    ${styleMixins_reset.boxSizing};
    position: relative;
    z-index: 3;
    padding: 0 20px 20px;
  `,
  detailsHref: react.css`
    ${styleMixins_reset.boxSizing};
    z-index: 3;
  `
});
const ZapCardCTA = (props) => {
  const { computedState, ariaHidden, href, isLink, target } = props;
  const ctaBody = isLink ? react.jsx(
    React.Fragment,
    null,
    react.jsx(
      components_navigation_Link_Link.Link,
      { css: Styles.ctaLink(computedState), href, target, tabIndex: ariaHidden ? -1 : void 0 },
      "Try it",
      react.jsx(
        "span",
        { css: Styles.ctaIcon(computedState), "data-zds": true },
        react.jsx(components_display_Icon_Icon.Icon, { isBlock: true, name: "arrowBigRight", size: 24 })
      )
    )
  ) : react.jsx(components_navigation_Button_Button.Button, { href: props.href, target, tabIndex: ariaHidden ? -1 : void 0, size: "medium" }, "Try it");
  return (
    // Add `aria-hidden` to avoid redundant CTAs if it's already handled by `link`.
    // Also add `tabIndex={-1}` to the `Link` or `Button` since nodes with `aria-hidden` on them
    // shouldn't contain interactive elements.
    react.jsx("div", { "aria-hidden": ariaHidden, css: Styles.ctaArea(computedState, props), "data-zds": true }, ctaBody)
  );
};
const defaultRenderAuthor = (props, computedState) => {
  if (!props.authorName) {
    return null;
  }
  const [first, ...rest] = props.authorName.split(/\s+/);
  const last = rest.pop();
  const lastInitial = last ? `${last[0]}.` : "";
  const name = `${first} ${lastInitial}`.trim();
  const author = react.jsx(
    "p",
    { "aria-hidden": true, css: Styles.authorByline(computedState), "data-zds": true },
    "by",
    " ",
    react.jsx("span", { css: Styles.authorName, "data-zds": true }, name)
  );
  return react.jsx(
    "div",
    { "aria-label": `by ${props.authorName}`, css: Styles.authorContent(computedState), "data-zds": true },
    react.jsx(components_display_Avatar_Avatar.Avatar, { "aria-hidden": true, isBlock: true, name: props.authorName, size: "small", url: props.authorAvatarUrl }),
    react.jsx(components_display_TooltipWrapper_TooltipWrapper.TooltipWrapper, { content: props.authorName, position: "southwest", shouldCorrectPosition: false }, () => author)
  );
};
const defaultRenderLabel = (props, computedState) => {
  const color = shouldBeColored(computedState) ? "inverted" : "default";
  return props.label && react.jsx(components_display_TooltipWrapper_TooltipWrapper.TooltipWrapper, { content: props.labelTooltip, position: "southeast", shouldCorrectPosition: false }, () => react.jsx(components_display_Tag_Tag.Tag, { variant: color }, props.label));
};
const defaultRenderMenu = (props, computedState, { closeMenu }) => {
  var _a, _b, _c;
  const hasDetailsMenuItem = props.detailsLink && isLineLayout(computedState) && isMediumLineLayoutWidth(computedState);
  if (!hasDetailsMenuItem && !hasMenu(props.menuItems)) {
    return null;
  }
  const renderMenuItem = (item) => {
    const onClick = (e) => {
      item.onClick && item.onClick(e, item);
      closeMenu();
    };
    const icon = item.icon && react.jsx(components_display_Icon_Icon.Icon, { name: item.icon, size: 24 });
    return react.jsx(
      components_forms_Menu_MenuItem.MenuItem,
      { href: item.href, key: item.text, onClick, rel: item.rel },
      react.jsx(
        "span",
        { css: Styles.menuItemInner, "data-zds": true },
        icon,
        react.jsx("span", { css: Styles.menuItemText, "data-zds": true }, item.text)
      )
    );
  };
  const items = ((_a = props.menuItems) !== null && _a !== void 0 ? _a : []).map(renderMenuItem);
  return react.jsx(
    "div",
    { css: Styles.menu(computedState), "data-zds": true },
    react.jsx(
      components_forms_Menu_Menu.Menu,
      { "aria-label": "Zap options" },
      react.jsx(
        "div",
        { css: Styles.menuInner, "data-zds": true },
        items,
        hasDetailsMenuItem && renderMenuItem({
          text: "See Zap details",
          icon: "miscList",
          href: (_b = props.detailsLink) === null || _b === void 0 ? void 0 : _b.href,
          rel: (_c = props.detailsLink) === null || _c === void 0 ? void 0 : _c.rel,
          onClick: (e) => {
            var _a2;
            return (_a2 = props.detailsLink) === null || _a2 === void 0 ? void 0 : _a2.onClick(e, computedState);
          }
        })
      )
    )
  );
};
const defaultRenderMetaInfo = (props, computedState) => {
  if (props.metaInfo) {
    return props.metaInfo;
  }
  const truncatedServices = computedState.uniqueServices.slice(0, 3);
  const countDiff = computedState.uniqueServices.length - truncatedServices.length;
  return [...truncatedServices, countDiff > 0 && `${countDiff} more`].filter(Boolean).join(" + ");
};
const ZapCard = ({ authorName, authorAvatarUrl, canClickOutsideToClose, children, color = DEFAULT_COLOR, detailsLink, href, hrefTarget = "_self", label, labelTooltip, layout = "auto", menuItems = [], metaInfo, onClick, renderAuthor, renderLabel, renderMenu, renderMetaInfo, services, shouldOverlayLink = true, title }) => {
  const colorValue = color || DEFAULT_COLOR;
  const allProps = {
    authorName,
    authorAvatarUrl,
    canClickOutsideToClose,
    children,
    color,
    detailsLink,
    href,
    hrefTarget,
    label,
    labelTooltip,
    layout,
    menuItems,
    metaInfo,
    onClick,
    renderAuthor,
    renderLabel,
    renderMenu,
    renderMetaInfo,
    services,
    title
  };
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const closeMenu = () => setIsMenuOpen(false);
  const [setMenuOutsideClickNode] = hooks_useClickOutside.useClickOutside({
    onClickOutside: closeMenu
  });
  let menuNode = null;
  const setMenuNode = (node) => {
    menuNode = node;
    setMenuOutsideClickNode(node);
  };
  const handleClick = (e) => {
    if (!menuNode || !menuNode.contains(e.target)) {
      onClick && onClick(e, computedState);
    }
  };
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [setExpansionOutsideClickNode] = hooks_useClickOutside.useClickOutside({
    onClickOutside: () => {
      if (canClickOutsideToClose) {
        setIsExpanded(false);
      }
    }
  });
  const { ref, width } = components_marketing_ZapCard_useResizeObserver.useResizeObserver();
  const uniqueServices = [
    ...new Set(services.map((service) => {
      return service.name;
    }))
  ];
  const computedState = Object.assign(Object.assign({
    isExpanded,
    isMenuOpen,
    layout
  }, getComputedColors(colorValue)), {
    width,
    uniqueServices
  });
  computedState.layout = layout !== "auto" ? layout : isCardLayoutWidth(computedState) ? "card" : "line";
  const canExpand = children && isLineLayout(computedState);
  const renderedMenu = renderMenu ? renderMenu(allProps, computedState, { closeMenu }) : defaultRenderMenu(allProps, computedState, { closeMenu });
  const renderedLabel = renderLabel ? renderLabel(allProps, computedState) : defaultRenderLabel(allProps, computedState);
  const renderedAuthor = renderAuthor ? renderAuthor(allProps, computedState) : defaultRenderAuthor(allProps, computedState);
  const renderedMetaInfo = renderMetaInfo ? renderMetaInfo(allProps, computedState) : defaultRenderMetaInfo(allProps, computedState);
  const DetailsLink = detailsLink && react.jsx(
    "span",
    { css: Styles.detailsHref, "data-zds": true },
    react.jsx(components_navigation_Link_Link.Link, { href: detailsLink.href, rel: detailsLink.rel, onClick: (e) => detailsLink === null || detailsLink === void 0 ? void 0 : detailsLink.onClick(e, computedState) }, "Details")
  );
  const link = shouldOverlayLink && (isCardLayout(computedState) || !children) && react.jsx(components_navigation_Link_Link.Link, { css: Styles.link, href, target: hrefTarget }, title);
  const details = canExpand && react.jsx(
    "details",
    { css: Styles.details, "data-testid": "ZapCard__details", open: isExpanded, "data-zds": true },
    react.jsx("summary", { css: Styles.summary, onClick: (e) => {
      e.preventDefault();
      setIsExpanded(!isExpanded);
    }, "data-zds": true }, "More details"),
    react.jsx("div", { css: Styles.detailsContent, "data-zds": true }, children)
  );
  const cta = isCardOrMegaLayout(computedState) ? react.jsx(ZapCardCTA, { computedState, ariaHidden: !!link, href, isLink: true, shouldOverlayLink, target: hrefTarget }) : isLineLayout(computedState) ? react.jsx(ZapCardCTA, { computedState, ariaHidden: !!link, href, isLink: false, shouldOverlayLink, target: hrefTarget }) : null;
  const serviceIconSize = isMegaLayout(computedState) ? "large" : "medium";
  return react.jsx(
    "article",
    { css: Styles.root(allProps, computedState), ref, "data-zds": true },
    react.jsx(
      "div",
      { css: Styles.inner(computedState, allProps), "data-testid": "ZapCard__inner", onClick: handleClick, ref: setExpansionOutsideClickNode, "data-zds": true },
      react.jsx(
        "div",
        { css: Styles.layout(computedState, allProps), "data-zds": true },
        react.jsx(
          "div",
          { css: Styles.titleArea(computedState), "data-zds": true },
          react.jsx(components_display_Heading_Heading.Heading, { css: Styles.title(computedState) }, title)
        ),
        renderedAuthor && react.jsx("div", { css: Styles.authorArea(computedState), "data-zds": true }, renderedAuthor),
        react.jsx(
          "div",
          { css: Styles.labelArea(computedState), "data-zds": true },
          renderedLabel,
          DetailsLink
        ),
        link,
        cta,
        react.jsx(
          "div",
          { css: Styles.menuArea(computedState), ref: setMenuNode, "data-zds": true },
          renderedMenu && react.jsx(
            "button",
            { "aria-expanded": isMenuOpen, "aria-label": "More options", css: Styles.menuToggle, onClick: () => setIsMenuOpen(!isMenuOpen), type: "button", "data-zds": true },
            react.jsx(components_display_Icon_Icon.Icon, { name: "navMoreHoriz", size: 32 })
          ),
          isMenuOpen && renderedMenu
        ),
        react.jsx(
          "div",
          { css: Styles.servicesArea, "data-zds": true },
          react.jsx(components_display_TooltipWrapper_TooltipWrapper.TooltipWrapper, { allowMultilineTooltip: true, content: uniqueServices.join(", "), position: "southwest", shouldCorrectPosition: false, tooltipId: "tooltip-zap-card" }, () => react.jsx(components_patterns_ServiceIcons_ServiceIcons.ServiceIcons, { "aria-label": `Zap uses ${uniqueServices.join(", ")}`, maxServices: 3, services, size: serviceIconSize, hasBorder: !shouldBeColored(computedState) }))
        ),
        react.jsx("div", { css: Styles.metaInfoArea(computedState), "data-zds": true }, renderedMetaInfo)
      ),
      details
    )
  );
};

exports.Styles = Styles;
exports.ZapCard = ZapCard;
//# sourceMappingURL=ZapCard.cjs.js.map
